import { useEffect, useState } from "react";
import { FiTrash, FiEdit2 } from "react-icons/fi";
import { API_URL } from '../../config/api';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import styles from "../../styles/FinanceView.module.css";

const CashWalletsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState("today");

  const COLORS = [
    "#4F46E5",
    "#22C55E",
    "#F97316",
    "#EF4444",
    "#06B6D4",
    "#A855F7",
  ];

  // Функция для выбора цвета по уникальному идентификатору склада
  const getColor = (str) => {
    if (!str) return COLORS[0];
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % COLORS.length;
    return COLORS[index];
  };

  useEffect(() => {
    const fetchCashWallets = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/wallets?type=cash`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await res.json();
        if (!res.ok) throw new Error("Ошибка загрузки");
        setData(json.data || []);
      } catch (error) {
        console.error("Ошибка cash wallets:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCashWallets();
  }, []);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString("ru-RU");
  };

  const handleEdit = (item) => console.log("Редактировать:", item);
  const handleDelete = (id) => console.log("Удалить:", id);

  // Данные для графика
  const chartData = data.map((item) => ({
    name: item.storage || `Склад ${item.id}`,
    balance: parseFloat(item.balance || 0),
    fill: getColor(item.storage || item.id.toString()),
  }));

  const shortenLabel = (label, maxLen = 12) =>
    label.length > maxLen ? label.slice(0, maxLen) + "…" : label;

  return (
    <div className={styles.financeContainer}>
      {/* Фильтр времени */}
      <div className={styles.timeFilter}>
        {["today", "yesterday", "week", "month"].map((f) => (
          <button
            key={f}
            className={`${styles.timeBtn} ${
              timeFilter === f ? styles.activeTimeBtn : ""
            }`}
            onClick={() => setTimeFilter(f)}
          >
            {f === "today"
              ? "Сегодня"
              : f === "yesterday"
              ? "Вчера"
              : f === "week"
              ? "Неделя"
              : "Месяц"}
          </button>
        ))}
      </div>

      {/* Таблица */}
      <div className={styles.tableWrapper}>
        {loading ? (
          <p className={styles.loading}>Загрузка...</p>
        ) : data.length === 0 ? (
          <p className={styles.empty}>Нет данных</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Название склада</th>
                <th>Дата создания</th>
                <th>Сумма</th>
                <th style={{ textAlign: "center" }}>Действия</th>
              </tr>
            </thead>

            <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  <td>{item.storage || "—"}</td>
                  <td>{item.inserted_at ? formatDate(item.inserted_at) : "—"}</td>
                  <td className={styles.balance}>
                    {parseFloat(item.balance || 0).toLocaleString()} ₸
                  </td>
                  <td className={styles.actionsCell}>
                    <button
                      className={styles.editBtn}
                      onClick={() => handleEdit(item)}
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(item.id)}
                    >
                      <FiTrash size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* График */}
      {data.length > 0 && (
        <div className={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
              barCategoryGap="25%"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tickFormatter={shortenLabel}
                interval={0}
                angle={-30}
                textAnchor="end"
              />
              <YAxis />
              <Tooltip formatter={(value) =>
                value.toLocaleString("ru-RU", { minimumFractionDigits: 2 }) + " ₸"
              } />
              <Legend />
              <Bar dataKey="balance" name="Баланс" maxBarSize={40} radius={[8, 8, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.fill}
                    stroke={entry.fill}
                    strokeWidth={1}
                  />
                ))}
                <LabelList
                  dataKey="balance"
                  position="top"
                  formatter={(val) => val.toLocaleString()}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CashWalletsTable;
